import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  CALENDAR_LIMITS,
  daysPerYear,
  describeRange,
  festivalsOn,
  formatCalendarDate,
  moonPhaseFraction,
  moonPhaseName,
  parseSortKey,
  parseWeekdayNames,
  seasonAt,
  toAbsoluteDay,
  weekdayName,
  type CalendarDetails,
  type CalendarSystem,
  type TimelineEventEntry,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { useReadOnly } from './readOnly.ts';
import { realmFile } from '../db/protocol.ts';
import { settings } from '../settings.ts';
import { ActionMenu } from '../components/ActionMenu.tsx';
import { dayMarks, drawable, monthGrid, monthOfDay, stepMonth, type MonthOf } from './calendarMonth.ts';

type CalendarTab = 'month' | 'year' | 'definition';

/** `?view=year` or `?view=definition` opens a calendar on that tab; the Month view is the default. */
export interface CalendarSearch {
  readonly view?: 'year' | 'definition';
}

/** The month on screen, and the day chosen in it. */
interface At {
  readonly month: MonthOf;
  readonly day: number | null;
}

/**
 * A realm's calendars (docs/PARITY-STEP-3-PLAN.md section 5), as the desktop's Calendar:
 * a card for each, the active one starred. An open calendar shows a month at a time
 * (docs/CHRONOLOGY-CALENDAR-REDESIGN-PLAN.md slice 3), with Go to in place of the desktop's
 * Date Lookup and a day's details on choosing it; its Definition holds the months, week,
 * seasons, moons and festivals, each edited in place and saved with the rest.
 */

const galleryRoute = getRouteApi('/_app/realm/$realmKey/calendars');
const calendarRoute = getRouteApi('/_app/realm/$realmKey/calendars/$calendarId');

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);

export function CalendarsPage() {
  const { calendars, activeId } = galleryRoute.useLoaderData();
  const { realmKey } = galleryRoute.useParams();
  const readOnly = useReadOnly();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [making, setMaking] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const { id } = await realms.createCalendar(file, name, '');
      await navigate({ to: '/realm/$realmKey/calendars/$calendarId', params: { realmKey, calendarId: id }, search: { view: 'definition' } });
    }, 'The calendar could not be made.');
  }

  return (
    <article className="document calendars-page">
      <p className="eyebrow">Calendar</p>
      <h1>Calendars</h1>
      <p className="lead">Your world's own calendars: months of any length, weeks with their own day names, seasons, moons and festivals.</p>
      {!readOnly && (
        <div className="actions">
          <button type="button" className="primary" onClick={() => setMaking(true)}>
            New calendar
          </button>
        </div>
      )}
      {making && (
        <form className="panel" aria-label="New calendar" onSubmit={(e) => void create(e)}>
          <label>
            Name
            <input value={name} required maxLength={CALENDAR_LIMITS.name} autoFocus placeholder="e.g. The Reckoning" onChange={(e) => setName(e.currentTarget.value)} />
          </label>
          <FormButtons submit="Make calendar" onCancel={() => setMaking(false)} />
        </form>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {calendars.length === 0 ? (
        <p className="meta">No calendars yet. Make one, then set it active to see the Chronology's dates in it.</p>
      ) : (
        <ul className="table-gallery">
          {calendars.map((c) => (
            <li key={c.id}>
              <Link to="/realm/$realmKey/calendars/$calendarId" params={{ realmKey, calendarId: c.id }}>
                {c.id === activeId && '★ '}
                {c.name}
              </Link>
              <span className="meta">
                {c.monthCount} {c.monthCount === 1 ? 'month' : 'months'} · {c.daysPerYear} days a year
                {c.description ? ` · ${c.description}` : ''}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="quiet"
                  onClick={() => void run(() => realms.setActiveCalendar(file, c.id === activeId ? null : c.id), 'The active calendar could not be changed.')}
                >
                  {c.id === activeId ? 'Unset active' : 'Set active'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

type Section = 'none' | 'details' | 'months' | 'week' | 'seasons' | 'moons' | 'festivals' | 'delete';

export function CalendarPage() {
  const view = calendarRoute.useLoaderData();
  const search = calendarRoute.useSearch();
  const { realmKey } = calendarRoute.useParams();
  const readOnly = useReadOnly();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('none');
  const [error, setError] = useState('');
  const [at, setAt] = useState<At | null>(null);

  const back = (
    <Link to="/realm/$realmKey/calendars" params={{ realmKey }}>
      All calendars
    </Link>
  );
  if (!view) {
    return (
      <article className="document">
        <p className="meta">That calendar no longer exists. It may have been deleted. {back}</p>
      </article>
    );
  }

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    const open = section;
    try {
      await work();
      // Closes what was saved, not a section opened meanwhile.
      setSection((now) => (now === open ? 'none' : now));
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  const remove = () =>
    run(async () => {
      await realms.deleteCalendar(file, view.id);
      // Back to all calendars, unless you've already gone elsewhere.
      if (router.state.location.pathname.endsWith(`/calendars/${view.id}`)) await navigate({ to: '/realm/$realmKey/calendars', params: { realmKey } });
    }, 'The calendar could not be deleted.');

  const calendar = view.calendar;
  const save = (change: Partial<CalendarDetails>) => run(() => realms.updateCalendar(file, view.id, { ...detailsOf(calendar!), ...change }), 'The calendar could not be saved.');
  const editing = (s: Section) => section === s;
  const toggle = (s: Section) => setSection(section === s ? 'none' : s);

  const tab: CalendarTab = search.view ?? 'month';
  const setTab = (next: CalendarTab) => void navigate({ to: '.', search: next === 'month' ? {} : { view: next }, replace: true });
  // The month (and day) on screen, shared by Month and Year; it opens on the latest event's month.
  const opening = (): At => ({
    month: calendar && view.lastEventDay !== null && drawable(calendar) ? monthOfDay(calendar, Number(view.lastEventDay)) : { year: calendar?.epochYear ?? 0, monthIndex: 0 },
    day: null,
  });
  const here = at ?? opening();
  // A month the calendar no longer has (it was edited) becomes its last.
  const current: At = calendar && here.month.monthIndex >= calendar.months.length ? { month: { ...here.month, monthIndex: Math.max(0, calendar.months.length - 1) }, day: null } : here;
  const name = calendar?.name ?? 'the calendar';

  return (
    <article className="document calendar-page">
      <p className="eyebrow">{back}</p>
      <div className="calendar-head">
        <h1>
          {view.active && '★ '}
          {calendar?.name ?? 'Calendar'}
        </h1>
        {calendar && (
          <span className="view-switch" role="group" aria-label="View">
            {(
              [
                ['month', 'Month'],
                ['year', 'Year'],
                ['definition', 'Definition'],
              ] as const
            ).map(([id, label]) => (
              <button key={id} type="button" className={tab === id ? 'tab selected' : 'tab'} aria-pressed={tab === id} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </span>
        )}
        {!readOnly && <ActionMenu
          label={`More for ${calendar?.name ?? 'this calendar'}`}
          actions={[
            {
              label: view.active ? 'Unset as active' : 'Set as active calendar',
              onSelect: () => void run(() => realms.setActiveCalendar(file, view.active ? null : view.id), 'The active calendar could not be changed.'),
            },
            ...(calendar ? [{ label: 'Edit details', onSelect: () => toggle('details') }] : []),
            { label: 'Delete calendar…', danger: true, onSelect: () => (settings()['general.confirmDeletes'] ? setSection('delete') : void remove()) },
          ]}
        />}
      </div>
      {calendar?.description && <p className="lead pre-line">{calendar.description}</p>}
      {view.active && <p className="meta">The active calendar: the Chronology shows dates in it too.</p>}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {editing('delete') && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${name}`}>
          <p>
            Delete “{calendar?.name ?? 'this calendar'}”, with its months, seasons, moons and festivals?
            {view.active ? ' The realm will have no active calendar.' : ''} This can't be undone.
          </p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove()}>
              Delete calendar
            </button>
            <button type="button" onClick={() => setSection('none')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {view.problem && <p className="callout">This calendar can't be used: {view.problem} Fix it on the desktop, or delete it.</p>}
      {calendar && (
        <>
          {editing('details') && <DetailsForm calendar={calendar} onSave={save} onCancel={() => setSection('none')} />}
          {tab === 'definition' ? (
            <CalendarSections calendar={calendar} editing={editing} toggle={toggle} save={save} close={() => setSection('none')} />
          ) : !drawable(calendar) ? (
            <p className="meta">This calendar has no days to show yet. Give it months under Definition.</p>
          ) : tab === 'year' ? (
            <YearView
              calendar={calendar}
              file={file}
              year={current.month.year}
              onYear={(year) => setAt({ month: { year, monthIndex: current.month.monthIndex }, day: null })}
              onPick={(month, day) => {
                setAt({ month, day });
                setTab('month');
              }}
            />
          ) : (
            <MonthView calendar={calendar} file={file} realmKey={realmKey} at={current} onAt={setAt} />
          )}
        </>
      )}
    </article>
  );
}

function detailsOf(c: CalendarSystem): CalendarDetails {
  return {
    name: c.name,
    description: c.description,
    months: c.months,
    daysPerWeek: c.daysPerWeek,
    weekdayNames: c.weekdayNames,
    hoursPerDay: c.hoursPerDay,
    epochYear: c.epochYear,
    seasons: c.seasons,
    moons: c.moons,
    festivals: c.festivals,
  };
}

interface SectionProps {
  readonly calendar: CalendarSystem;
  readonly editing: (s: Section) => boolean;
  readonly toggle: (s: Section) => void;
  readonly save: (change: Partial<CalendarDetails>) => Promise<void>;
  readonly close: () => void;
}

function CalendarSections({ calendar, editing, toggle, save, close }: SectionProps) {
  const monthName = (i: number) => calendar.months[i]?.name ?? `(month ${i + 1}, not in the year)`;
  return (
    <>
      <CalendarSection title={`Months (${daysPerYear(calendar)} days a year)`} onEdit={() => toggle('months')}>
        {editing('months') ? (
          <MonthsForm calendar={calendar} onSave={save} onCancel={close} />
        ) : (
          <ol className="calendar-list">
            {calendar.months.map((m, i) => (
              <li key={i}>
                {m.name} <span className="meta">{m.lengthInDays} days</span>
              </li>
            ))}
          </ol>
        )}
      </CalendarSection>

      <CalendarSection title="Week, day and epoch" onEdit={() => toggle('week')}>
        {editing('week') ? (
          <WeekForm calendar={calendar} onSave={save} onCancel={close} />
        ) : (
          <dl className="calendar-facts">
            <dt>Days in a week</dt>
            <dd>{calendar.daysPerWeek}</dd>
            <dt>Weekdays</dt>
            <dd>{calendar.weekdayNames.length > 0 ? calendar.weekdayNames.map((n, i) => n || `Day ${i + 1}`).join(', ') : 'Not named'}</dd>
            <dt>Hours in a day</dt>
            <dd>{calendar.hoursPerDay}</dd>
            <dt>Epoch year</dt>
            <dd>{calendar.epochYear} (the year day 0 falls in)</dd>
          </dl>
        )}
      </CalendarSection>

      <CalendarSection title="Seasons" onEdit={() => toggle('seasons')}>
        {editing('seasons') ? (
          <SeasonsForm calendar={calendar} onSave={save} onCancel={close} />
        ) : calendar.seasons.length === 0 ? (
          <p className="meta">No seasons.</p>
        ) : (
          <ul className="calendar-list">
            {calendar.seasons.map((s, i) => (
              <li key={i}>
                {s.name}{' '}
                <span className="meta">
                  {monthName(s.startMonth)} {s.startDay} – {monthName(s.endMonth)} {s.endDay}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CalendarSection>

      <CalendarSection title="Moons" onEdit={() => toggle('moons')}>
        {editing('moons') ? (
          <MoonsForm calendar={calendar} onSave={save} onCancel={close} />
        ) : calendar.moons.length === 0 ? (
          <p className="meta">No moons.</p>
        ) : (
          <ul className="calendar-list">
            {calendar.moons.map((m, i) => (
              <li key={i}>
                {m.name}{' '}
                <span className="meta">
                  a cycle of {m.cycleLengthDays} days, new on day {m.phaseOffsetDays}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CalendarSection>

      <CalendarSection title="Festivals and holidays" onEdit={() => toggle('festivals')}>
        {editing('festivals') ? (
          <FestivalsForm calendar={calendar} onSave={save} onCancel={close} />
        ) : calendar.festivals.length === 0 ? (
          <p className="meta">No festivals or holidays.</p>
        ) : (
          <ul className="calendar-list">
            {calendar.festivals.map((f, i) => (
              <li key={i}>
                {f.name}{' '}
                <span className="meta">
                  {monthName(f.monthIndex)} {f.day}, {f.year === null ? 'every year' : `Year ${f.year} only`}
                  {f.description ? ` · ${f.description}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CalendarSection>
    </>
  );
}

function CalendarSection({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  const readOnly = useReadOnly();
  return (
    <section className="calendar-section" aria-label={title.replace(/ \(.*\)$/, '')}>
      <header>
        <h2>{title}</h2>
        {!readOnly && (
          <button type="button" className="quiet" onClick={onEdit}>
            Edit
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

// ---- the forms: each edits one part, saved with the rest ----

interface FormProps {
  readonly calendar: CalendarSystem;
  readonly onSave: (change: Partial<CalendarDetails>) => Promise<void>;
  readonly onCancel: () => void;
}

/** A number typed in a field: NaN when it isn't one, which the save refuses with a message. */
const num = (typed: string) => (typed.trim() === '' ? Number.NaN : Number(typed));

function DetailsForm({ calendar, onSave, onCancel }: FormProps) {
  const [name, setName] = useState(calendar.name);
  const [description, setDescription] = useState(calendar.description);
  return (
    <Form label="Edit details" onSubmit={() => onSave({ name, description })} onCancel={onCancel}>
      <label>
        Name
        <input value={name} required maxLength={CALENDAR_LIMITS.name} onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={CALENDAR_LIMITS.description} onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
    </Form>
  );
}

function MonthsForm({ calendar, onSave, onCancel }: FormProps) {
  const [rows, setRows] = useState(calendar.months.map((m) => ({ name: m.name, length: String(m.lengthInDays) })));
  return (
    <Form label="Edit months" onSubmit={() => onSave({ months: rows.map((r) => ({ name: r.name, lengthInDays: num(r.length) })) })} onCancel={onCancel}>
      <Rows
        rows={rows}
        setRows={setRows}
        noun="month"
        blank={{ name: `Month ${rows.length + 1}`, length: '30' }}
        min={1}
        render={(r, i, set) => (
          <>
            <label>
              Name of month {i + 1}
              <input value={r.name} maxLength={CALENDAR_LIMITS.name} onChange={(e) => set({ ...r, name: e.currentTarget.value })} />
            </label>
            <label className="short">
              Days in month {i + 1}
              <input value={r.length} inputMode="numeric" onChange={(e) => set({ ...r, length: e.currentTarget.value })} />
            </label>
          </>
        )}
      />
      <p className="meta">Removing a month moves every later day: dates already written keep their sort keys, so they fall on other days.</p>
    </Form>
  );
}

function WeekForm({ calendar, onSave, onCancel }: FormProps) {
  const [week, setWeek] = useState(String(calendar.daysPerWeek));
  const [names, setNames] = useState(calendar.weekdayNames.join(', '));
  const [hours, setHours] = useState(String(calendar.hoursPerDay));
  const [epoch, setEpoch] = useState(String(calendar.epochYear));
  return (
    <Form
      label="Edit week, day and epoch"
      onSubmit={() => onSave({ daysPerWeek: num(week), weekdayNames: parseWeekdayNames(names), hoursPerDay: num(hours), epochYear: num(epoch) })}
      onCancel={onCancel}
    >
      <label className="short">
        Days per week
        <input value={week} inputMode="numeric" onChange={(e) => setWeek(e.currentTarget.value)} />
      </label>
      <label>
        Weekday names
        <input value={names} placeholder="Comma-separated, e.g. Sunday, Moonday, Tyrsday" onChange={(e) => setNames(e.currentTarget.value)} />
      </label>
      <label className="short">
        Hours per day
        <input value={hours} inputMode="numeric" onChange={(e) => setHours(e.currentTarget.value)} />
      </label>
      <label className="short">
        Epoch year (the year of day 0)
        <input value={epoch} inputMode="numeric" onChange={(e) => setEpoch(e.currentTarget.value)} />
      </label>
    </Form>
  );
}

function MonthSelect({ calendar, value, label, onChange }: { calendar: CalendarSystem; value: number; label: string; onChange: (i: number) => void }) {
  return (
    <label>
      {label}
      <select value={value} onChange={(e) => onChange(Number(e.currentTarget.value))}>
        {calendar.months.map((m, i) => (
          <option key={i} value={i}>
            {m.name}
          </option>
        ))}
        {value >= calendar.months.length && <option value={value}>(month {value + 1}, not in the year)</option>}
      </select>
    </label>
  );
}

function SeasonsForm({ calendar, onSave, onCancel }: FormProps) {
  const [rows, setRows] = useState(calendar.seasons.map((s) => ({ ...s, startDayText: String(s.startDay), endDayText: String(s.endDay) })));
  return (
    <Form
      label="Edit seasons"
      onSubmit={() =>
        onSave({ seasons: rows.map((r) => ({ name: r.name, startMonth: r.startMonth, startDay: num(r.startDayText), endMonth: r.endMonth, endDay: num(r.endDayText) })) })
      }
      onCancel={onCancel}
    >
      <p className="meta">A season that starts after it ends runs on past the year's end, as a winter would.</p>
      <Rows
        rows={rows}
        setRows={setRows}
        noun="season"
        blank={{ name: '', startMonth: 0, startDay: 1, endMonth: 0, endDay: 1, startDayText: '1', endDayText: '1' }}
        render={(r, i, set) => (
          <>
            <label>
              Name of season {i + 1}
              <input value={r.name} maxLength={CALENDAR_LIMITS.name} placeholder="e.g. Winter" onChange={(e) => set({ ...r, name: e.currentTarget.value })} />
            </label>
            <MonthSelect calendar={calendar} label={`Starts in (season ${i + 1})`} value={r.startMonth} onChange={(startMonth) => set({ ...r, startMonth })} />
            <label className="short">
              Start day
              <input value={r.startDayText} inputMode="numeric" onChange={(e) => set({ ...r, startDayText: e.currentTarget.value })} />
            </label>
            <MonthSelect calendar={calendar} label={`Ends in (season ${i + 1})`} value={r.endMonth} onChange={(endMonth) => set({ ...r, endMonth })} />
            <label className="short">
              End day
              <input value={r.endDayText} inputMode="numeric" onChange={(e) => set({ ...r, endDayText: e.currentTarget.value })} />
            </label>
          </>
        )}
      />
    </Form>
  );
}

function MoonsForm({ calendar, onSave, onCancel }: FormProps) {
  const [rows, setRows] = useState(calendar.moons.map((m) => ({ name: m.name, cycle: String(m.cycleLengthDays), offset: String(m.phaseOffsetDays) })));
  return (
    <Form label="Edit moons" onSubmit={() => onSave({ moons: rows.map((r) => ({ name: r.name, cycleLengthDays: num(r.cycle), phaseOffsetDays: num(r.offset) })) })} onCancel={onCancel}>
      <Rows
        rows={rows}
        setRows={setRows}
        noun="moon"
        blank={{ name: '', cycle: '29.5', offset: '0' }}
        render={(r, i, set) => (
          <>
            <label>
              Name of moon {i + 1}
              <input value={r.name} maxLength={CALENDAR_LIMITS.name} placeholder="e.g. Luna" onChange={(e) => set({ ...r, name: e.currentTarget.value })} />
            </label>
            <label className="short">
              Cycle (days)
              <input value={r.cycle} inputMode="decimal" onChange={(e) => set({ ...r, cycle: e.currentTarget.value })} />
            </label>
            <label className="short">
              New on day
              <input value={r.offset} inputMode="decimal" onChange={(e) => set({ ...r, offset: e.currentTarget.value })} />
            </label>
          </>
        )}
      />
    </Form>
  );
}

function FestivalsForm({ calendar, onSave, onCancel }: FormProps) {
  const [rows, setRows] = useState(
    calendar.festivals.map((f) => ({ name: f.name, description: f.description, monthIndex: f.monthIndex, day: String(f.day), every: f.year === null, year: String(f.year ?? 1) })),
  );
  return (
    <Form
      label="Edit festivals and holidays"
      onSubmit={() =>
        onSave({ festivals: rows.map((r) => ({ name: r.name, description: r.description, monthIndex: r.monthIndex, day: num(r.day), year: r.every ? null : num(r.year) })) })
      }
      onCancel={onCancel}
    >
      <Rows
        rows={rows}
        setRows={setRows}
        noun="festival"
        blank={{ name: '', description: '', monthIndex: 0, day: '1', every: true, year: String(calendar.epochYear) }}
        render={(r, i, set) => (
          <>
            <label>
              Name of festival {i + 1}
              <input value={r.name} maxLength={CALENDAR_LIMITS.name} placeholder="e.g. Midwinter" onChange={(e) => set({ ...r, name: e.currentTarget.value })} />
            </label>
            <MonthSelect calendar={calendar} label={`Month (festival ${i + 1})`} value={r.monthIndex} onChange={(monthIndex) => set({ ...r, monthIndex })} />
            <label className="short">
              Day
              <input value={r.day} inputMode="numeric" onChange={(e) => set({ ...r, day: e.currentTarget.value })} />
            </label>
            <label className="check">
              <input type="checkbox" checked={r.every} onChange={(e) => set({ ...r, every: e.currentTarget.checked })} />
              Every year
            </label>
            {!r.every && (
              <label className="short">
                Year only
                <input value={r.year} inputMode="numeric" onChange={(e) => set({ ...r, year: e.currentTarget.value })} />
              </label>
            )}
            <label className="grow">
              Description
              <input value={r.description} maxLength={CALENDAR_LIMITS.description} placeholder="Optional" onChange={(e) => set({ ...r, description: e.currentTarget.value })} />
            </label>
          </>
        )}
      />
    </Form>
  );
}

/** A list of rows to change, reorder, remove and add to, as the desktop's lists with Edit, Delete and + Add. */
function Rows<T>({
  rows,
  setRows,
  noun,
  blank,
  min = 0,
  render,
}: {
  rows: readonly T[];
  setRows: (rows: T[]) => void;
  noun: string;
  blank: T;
  min?: number;
  render: (row: T, index: number, set: (row: T) => void) => ReactNode;
}) {
  const replace = (i: number, row: T) => setRows(rows.map((r, j) => (j === i ? row : r)));
  const move = (i: number, by: number) => {
    const next = [...rows];
    [next[i], next[i + by]] = [next[i + by]!, next[i]!];
    setRows(next);
  };
  return (
    <>
      {rows.length === 0 && <p className="meta">None yet.</p>}
      <ol className="calendar-rows">
        {rows.map((row, i) => (
          <li key={i}>
            {render(row, i, (r) => replace(i, r))}
            <span className="row-buttons">
              <button type="button" className="quiet" disabled={i === 0} aria-label={`Move ${noun} ${i + 1} up`} onClick={() => move(i, -1)}>
                ↑
              </button>
              <button type="button" className="quiet" disabled={i === rows.length - 1} aria-label={`Move ${noun} ${i + 1} down`} onClick={() => move(i, 1)}>
                ↓
              </button>
              <button type="button" className="quiet" disabled={rows.length <= min} aria-label={`Remove ${noun} ${i + 1}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                ✕
              </button>
            </span>
          </li>
        ))}
      </ol>
      <button type="button" onClick={() => setRows([...rows, blank])}>
        + Add {noun}
      </button>
    </>
  );
}

function Form({ label, onSubmit, onCancel, children }: { label: string; onSubmit: () => Promise<void>; onCancel: () => void; children: ReactNode }) {
  return (
    <form
      className="panel calendar-form"
      aria-label={label}
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      {children}
      <FormButtons submit="Save" onCancel={onCancel} />
    </form>
  );
}

function FormButtons({ submit, onCancel }: { submit: string; onCancel: () => void }) {
  return (
    <div className="actions">
      <button type="submit" className="primary">
        {submit}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

// ---- the month view ----

function MonthView({ calendar, file, realmKey, at, onAt }: { calendar: CalendarSystem; file: string; realmKey: string; at: At; onAt: (at: At) => void }) {
  const shown = at.month;
  const selected = at.day;
  const setSelected = (day: number | null) => onAt({ month: shown, day });
  const [events, setEvents] = useState<readonly TimelineEventEntry[] | null>(null);
  const [problem, setProblem] = useState('');
  const grid = monthGrid(calendar, shown);
  const monthName = calendar.months[shown.monthIndex]!.name;
  const title = `${monthName}, Year ${shown.year}`;

  useEffect(() => {
    let current = true;
    setEvents(null);
    realms
      .eventsBetween(file, BigInt(grid.first), BigInt(grid.last))
      .then((found) => current && setEvents(found))
      .catch((e: unknown) => current && setProblem(message(e, 'The events could not be read.')));
    return () => {
      current = false;
    };
  }, [file, grid.first, grid.last]);

  // An event shows on the day it starts, or on the 1st if it started before the month.
  const byDay = new Map<number, { entry: TimelineEventEntry; continues: boolean }[]>();
  for (const entry of events ?? []) {
    const start = Number(entry.event.start.sortKey);
    const day = Math.max(start, grid.first);
    byDay.set(day, [...(byDay.get(day) ?? []), { entry, continues: start < grid.first }]);
  }

  function go(month: MonthOf, day: number | null) {
    setProblem('');
    onAt({ month, day });
  }

  const season = seasonAt(calendar, grid.first);
  return (
    <section className="month-view" aria-label="Month">
      <div className="month-nav">
        <button type="button" className="quiet" aria-label="Previous month" onClick={() => go(stepMonth(calendar, shown, -1), null)}>
          ◀
        </button>
        <h2>{title}</h2>
        <button type="button" className="quiet" aria-label="Next month" onClick={() => go(stepMonth(calendar, shown, 1), null)}>
          ▶
        </button>
        <GoTo calendar={calendar} shown={shown} onGo={go} onProblem={setProblem} />
      </div>
      <p className="meta">
        {season ? `${season.name} · ` : ''}
        {daysPerYear(calendar)} days a year
      </p>
      {problem && (
        <p className="notice-error" role="alert">
          {problem}
        </p>
      )}
      <table className="month-grid" aria-label={title}>
        <thead>
          <tr>
            {grid.weekdays.map((w, i) => (
              <th key={i} scope="col">
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.weeks.map((week, i) => (
            <tr key={i}>
              {week.map((day, j) =>
                day === null ? (
                  <td key={j} className="month-blank" />
                ) : (
                  <td key={j} className={day === selected ? 'selected' : undefined}>
                    <button
                      type="button"
                      className="day-number"
                      aria-label={`${monthName} ${day - grid.first + 1}, Year ${shown.year}`}
                      aria-pressed={day === selected}
                      onClick={() => setSelected(day === selected ? null : day)}
                    >
                      {day - grid.first + 1}
                    </button>
                    <DayMarksList calendar={calendar} day={day} />
                    {(byDay.get(day) ?? []).map(({ entry: { timeline, event }, continues }) => (
                      <Link
                        key={event.id}
                        className={continues ? 'day-event continues' : 'day-event'}
                        to="/realm/$realmKey/chronology"
                        params={{ realmKey }}
                        search={{ timeline: timeline.id }}
                        title={`${describeRange(event.start, event.end)} · ${timeline.name}`}
                      >
                        {continues ? '↳ ' : ''}
                        {event.title}
                      </Link>
                    ))}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {selected !== null && <DayDetails calendar={calendar} day={selected} events={events} realmKey={realmKey} />}
    </section>
  );
}

/** Every month small, on one screen, with festivals and events as dots (slice 4). */
function YearView({
  calendar,
  file,
  year,
  onYear,
  onPick,
}: {
  calendar: CalendarSystem;
  file: string;
  year: number;
  onYear: (year: number) => void;
  onPick: (month: MonthOf, day: number | null) => void;
}) {
  const months = calendar.months.map((_, monthIndex) => monthGrid(calendar, { year, monthIndex }));
  const first = months[0]!.first;
  const last = months[months.length - 1]!.last;
  const [events, setEvents] = useState<readonly TimelineEventEntry[]>([]);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let current = true;
    setEvents([]);
    realms
      .eventsBetween(file, BigInt(first), BigInt(last))
      .then((found) => current && setEvents(found))
      .catch((e: unknown) => current && setProblem(message(e, 'The events could not be read.')));
    return () => {
      current = false;
    };
  }, [file, first, last]);

  // Each event on the day it starts, or the year's first day if it began before.
  const named = new Map<number, string[]>();
  for (const { event } of events) {
    const day = Math.max(Number(event.start.sortKey), first);
    named.set(day, [...(named.get(day) ?? []), event.title]);
  }

  return (
    <section className="year-view" aria-label="Year">
      <div className="month-nav">
        <button type="button" className="quiet" aria-label="Previous year" onClick={() => onYear(year - 1)}>
          ◀
        </button>
        <h2>Year {year}</h2>
        <button type="button" className="quiet" aria-label="Next year" onClick={() => onYear(year + 1)}>
          ▶
        </button>
        <span className="meta year-key">
          <span className="dot festival" aria-hidden="true" /> festival <span className="dot event" aria-hidden="true" /> event
        </span>
      </div>
      {problem && (
        <p className="notice-error" role="alert">
          {problem}
        </p>
      )}
      <div className="year-grid">
        {months.map((grid, monthIndex) => {
          const name = calendar.months[monthIndex]!.name;
          return (
            <div key={monthIndex} className="year-month">
              <h3>
                <button type="button" className="link-button" onClick={() => onPick({ year, monthIndex }, null)}>
                  {name}
                </button>
              </h3>
              <table aria-label={name}>
                <thead>
                  <tr>
                    {grid.weekdays.map((w, i) => (
                      <th key={i} scope="col" title={w}>
                        {w.slice(0, 2)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.weeks.map((week, i) => (
                    <tr key={i}>
                      {week.map((day, j) => {
                        if (day === null) return <td key={j} />;
                        const festivals = festivalsOn(calendar, day).map((f) => f.name);
                        const happened = named.get(day) ?? [];
                        const notes = [...festivals.map((f) => `🎉 ${f}`), ...happened];
                        const number = day - grid.first + 1;
                        return (
                          <td key={j}>
                            <button
                              type="button"
                              className={['year-day', festivals.length ? 'has-festival' : '', happened.length ? 'has-event' : ''].join(' ').trim()}
                              aria-label={`${name} ${number}, Year ${year}${notes.length ? `: ${notes.join(', ')}` : ''}`}
                              title={notes.join('\n') || undefined}
                              onClick={() => onPick({ year, monthIndex }, day)}
                            >
                              {number}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DayMarksList({ calendar, day }: { calendar: CalendarSystem; day: number }) {
  const marks = dayMarks(calendar, day);
  const items = [
    ...marks.moons.map((m) => ({ text: `${m.phase === 'full' ? '●' : '○'} ${m.name} ${m.phase}`, kind: 'moon' })),
    ...marks.festivals.map((f) => ({ text: `🎉 ${f.name}`, kind: 'festival' })),
    ...marks.seasonsStarting.map((n) => ({ text: `${n} begins`, kind: 'season' })),
    ...marks.seasonsEnding.map((n) => ({ text: `${n} ends`, kind: 'season' })),
  ];
  if (items.length === 0) return null;
  return (
    <ul className="day-marks">
      {items.map((item, i) => (
        <li key={i} className={item.kind}>
          {item.text}
        </li>
      ))}
    </ul>
  );
}

/** Go to a year, month and (if given) day; or to a sort key, as the desktop's Date Lookup did. */
function GoTo({ calendar, shown, onGo, onProblem }: { calendar: CalendarSystem; shown: MonthOf; onGo: (month: MonthOf, day: number | null) => void; onProblem: (p: string) => void }) {
  const [year, setYear] = useState(String(shown.year));
  const [month, setMonth] = useState(shown.monthIndex);
  const [day, setDay] = useState('');
  const [key, setKey] = useState('');
  // Follows ◀ ▶, so Go to starts from the month on screen.
  useEffect(() => {
    setYear(String(shown.year));
    setMonth(shown.monthIndex);
  }, [shown.year, shown.monthIndex]);

  function byDate(e: FormEvent) {
    e.preventDefault();
    const y = num(year);
    const length = calendar.months[month]?.lengthInDays ?? 1;
    const d = day.trim() === '' ? null : num(day);
    if (!Number.isInteger(y) || Math.abs(y) > 2_000_000_000 || (d !== null && (!Number.isInteger(d) || d < 1 || d > length))) {
      onProblem(`Choose a year, and a day from 1 to ${length} (or no day, for the whole month).`);
      return;
    }
    const absolute = toAbsoluteDay(calendar, y, month, d ?? 1);
    if (!Number.isSafeInteger(absolute)) return onProblem('That date is too far out to show.');
    onGo({ year: y, monthIndex: month }, d === null ? null : absolute);
  }

  function byKey(e: FormEvent) {
    e.preventDefault();
    try {
      const absolute = Number(parseSortKey(key));
      onGo(monthOfDay(calendar, absolute), absolute);
    } catch (error) {
      onProblem((error as Error).message);
    }
  }

  return (
    <div className="go-to">
      <form className="date-picker" aria-label="Go to a date" onSubmit={byDate}>
        <label>
          Year
          <input value={year} inputMode="numeric" size={6} onChange={(e) => setYear(e.currentTarget.value)} />
        </label>
        <MonthSelect calendar={calendar} label="Month" value={month} onChange={setMonth} />
        <label>
          Day
          <input value={day} inputMode="numeric" size={3} onChange={(e) => setDay(e.currentTarget.value)} />
        </label>
        <button type="submit">Go</button>
      </form>
      <form className="date-picker" aria-label="Go to a sort key" onSubmit={byKey}>
        <label>
          Sort key
          <input value={key} inputMode="numeric" size={8} onChange={(e) => setKey(e.currentTarget.value)} />
        </label>
        <button type="submit">Go</button>
      </form>
    </div>
  );
}

function DayDetails({ calendar, day, events, realmKey }: { calendar: CalendarSystem; day: number; events: readonly TimelineEventEntry[] | null; realmKey: string }) {
  const season = seasonAt(calendar, day);
  const on = (events ?? []).filter(({ event }) => BigInt(day) >= event.start.sortKey && BigInt(day) <= (event.end ?? event.start).sortKey);
  return (
    <div className="lookup-result" role="status">
      <h3>
        {formatCalendarDate(calendar, day)} <span className="meta">(sort key {day})</span>
      </h3>
      <ul className="calendar-list">
        <li>Weekday: {weekdayName(calendar, day)}</li>
        {season && <li>Season: {season.name}</li>}
        {calendar.moons.map((moon, i) => (
          <li key={i}>
            {moon.name}: {moonPhaseName(moonPhaseFraction(moon, day))}
          </li>
        ))}
        {festivalsOn(calendar, day).map((f, i) => (
          <li key={`f${i}`}>
            <span className="chip">🎉 {f.name}</span>
          </li>
        ))}
      </ul>
      <h4>What happened on this day</h4>
      {events === null ? (
        <p className="meta">Reading…</p>
      ) : on.length === 0 ? (
        <p className="meta">No recorded events on this day.</p>
      ) : (
        <ul className="event-list">
          {on.map(({ timeline, event }) => (
            <li key={event.id}>
              <div className="event-head">
                <Link to="/realm/$realmKey/chronology" params={{ realmKey }} search={{ timeline: timeline.id }}>
                  {event.title}
                </Link>
                <span className="meta">{describeRange(event.start, event.end)}</span>
                <span className="chip">{timeline.name}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
